import { Box, Grid, LinearProgress, Paper, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/react";
import { useFormikContext } from "formik";
import React from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { QuestionProps } from "../../components/Question/interfaces";
import { Answer, FileUpload } from "../../globals/types";
import useToken from "../../hooks/useToken";
import { authHeader, formsFetch, unwrap } from "../../lib/formsApi";
import { initialValue } from "../Form/helpers";

import DropZone from "./DropZone";
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE } from "./files";
import { FilesList } from "./FilesList";
import { UploadInput, UploadWrapper } from "./styled";

type Props = {
    answer?: Answer;
    saveDraft: QuestionProps["saveDraft"];
    questionId: string;
    uploadType: FileUpload;
    disabled?: boolean;
    setSubmitBlocked: React.Dispatch<React.SetStateAction<boolean>>;
    multiple?: boolean;
    accept?: string;
};

export const Files: React.FC<Props> = ({
    answer,
    saveDraft,
    questionId,
    uploadType,
    disabled,
    setSubmitBlocked,
    multiple = true,
    accept = ACCEPTED_FILE_EXTENSIONS,
}) => {
    const { getFieldMeta, setFieldError, setFieldTouched, setFieldValue } =
        useFormikContext<Record<string, initialValue>>();
    const { touched, error } = getFieldMeta(`${questionId}.fileNames`);
    const { formatMessage } = useIntl();
    const { id: assessmentId } = useParams<{ id: string }>();
    const { token } = useToken();
    const [loading, setLoading] = React.useState(false);

    const canUploadMoreFiles = multiple || (answer?.fileNames.length ?? 0) < 1;

    const handleFileChange = async (pendingFiles: FileList | null) => {
        if (!pendingFiles || pendingFiles.length === 0) {
            setFieldTouched(`${questionId}.fileNames`, true, true);
            return;
        }

        setFieldTouched(`${questionId}.fileNames`, true, false);

        let currentAnswerId = answer?.id;
        if (!currentAnswerId) {
            currentAnswerId = await saveDraft(undefined, undefined); // Save again to get the ID
        }
        if (!currentAnswerId) {
            setFieldError(
                `${questionId}.fileNames`,
                formatMessage({ defaultMessage: "Upload failed. Please try again." })
            );
            return;
        }

        const blockedFiles: string[] = [];
        const invalidExtensionFiles: string[] = [];
        const validPendingFiles: { name: string; file: File }[] = [];

        const acceptedExtensions = accept.split(",");

        Array.from(pendingFiles).forEach((selectedFile) => {
            const fileName = selectedFile.name;
            const fileExtension = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();

            if (!acceptedExtensions.includes(fileExtension)) {
                invalidExtensionFiles.push(fileName);
            } else if (selectedFile.size > MAX_FILE_SIZE) {
                blockedFiles.push(fileName);
            } else {
                validPendingFiles.push({ name: fileName, file: selectedFile });
            }
        });

        if (invalidExtensionFiles.length > 0) {
            const invalidFileList = invalidExtensionFiles.join(", ");
            setFieldError(
                `${questionId}.fileNames`,
                `${invalidFileList} ${formatMessage({
                    defaultMessage: "cannot be uploaded because of unsupported file type",
                })}`
            );
        } else if (blockedFiles.length > 0) {
            const badFileList = blockedFiles.join(", ");
            setFieldError(
                `${questionId}.fileNames`,
                `${badFileList} ${formatMessage({
                    defaultMessage: "cannot be uploaded because of file size (> 30MB)",
                })}`
            );
        } else if (
            error &&
            (error.includes("cannot be uploaded because of file size") ||
                error.includes("cannot be uploaded because of unsupported file type"))
        ) {
            setFieldError(`${questionId}.fileNames`, undefined);
        }

        if (validPendingFiles.length < 1) {
            return;
        }

        // If singleFile is true, only process the first file and replace existing files
        const filesToProcess = multiple ? validPendingFiles : validPendingFiles.slice(0, 1);

        // Show warning if user selected multiple files but singleFile is true (shouldn't hit)
        if (multiple && validPendingFiles.length > 1) {
            setFieldError(
                `${questionId}.fileNames`,
                formatMessage({
                    defaultMessage: "Only one file can be uploaded at a time. The first file will be used.",
                })
            );
        }

        const uploadTasks = filesToProcess.map(async (pendingFileInfo) => {
            if (!currentAnswerId) {
                // Should not happen if initial draft save worked
                Sentry.captureMessage("Upload skipped: currentAnswerId is missing for file", {
                    extra: {
                        fileName: pendingFileInfo.name,
                        context: "File upload error",
                    },
                });
                return null;
            }

            try {
                const { url, uniqueFileName, requestHeaders } = unwrap(
                    await formsFetch.POST("/api/files/upload-url", {
                        headers: authHeader(token),
                        body: {
                            referenceId: currentAnswerId,
                            fileName: pendingFileInfo.name,
                            metaJson: JSON.stringify({
                                origin: "assessment",
                                questionId: questionId,
                                assessmentId: assessmentId,
                            }),
                        },
                    }),
                    "create upload url"
                );

                const headers = new Headers();
                requestHeaders?.forEach((header) => {
                    headers.set(header.key, header.value);
                });

                const uploadResponse = await fetch(url, {
                    method: "PUT",
                    headers: headers,
                    body: pendingFileInfo.file,
                });
                if (!uploadResponse.ok) {
                    throw new Error(`Upload request failed with status ${uploadResponse.status}`);
                }
                return uniqueFileName;
            } catch (uploadError) {
                Sentry.captureException(uploadError, {
                    extra: {
                        fileName: pendingFileInfo.name,
                        context: "File upload error",
                    },
                });
                return null;
            }
        });

        const uploadResults = await Promise.all(uploadTasks);
        const successfullyUploadedFileNames = uploadResults.filter((name): name is string => typeof name === "string");

        if (successfullyUploadedFileNames.length > 0) {
            let newFileNames: string[];
            if (!multiple) {
                // For single file, replace any existing files with the new one
                newFileNames = successfullyUploadedFileNames;
            } else {
                // For multiple files, append to existing files
                newFileNames = [...(answer?.fileNames ?? []), ...successfullyUploadedFileNames];
            }
            await saveDraft(undefined, newFileNames);
            setFieldValue(`${questionId}.fileNames`, newFileNames, true); // Update Formik and trigger validation
        } else if (filesToProcess.length > 0 && successfullyUploadedFileNames.length === 0) {
            if (!error || !error.includes("cannot be uploaded because of file size")) {
                setFieldError(
                    `${questionId}.fileNames`,
                    formatMessage({ defaultMessage: "File upload failed. Please try again." })
                );
            }
        }
        // Final validation will be triggered by setFieldValue or previous setFieldTouched.
    };

    return (
        <Grid flexDirection="column" width="100%">
            {answer && answer.fileNames.length > 0 && (
                <Grid>
                    <FilesList
                        disabled={disabled}
                        fileNames={answer?.fileNames}
                        answerId={answer?.id}
                        setLoading={setLoading}
                        onFileRemove={(deleteFileName) => {
                            saveDraft(
                                undefined,
                                answer?.fileNames.filter((fileName) => fileName !== deleteFileName)
                            );
                        }}
                    />
                </Grid>
            )}
            {loading && (
                <Grid
                    component={Paper}
                    variant="outlined"
                    sx={{
                        py: 2.5,
                        px: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <LinearProgress
                        sx={{
                            width: "100%",
                            borderRadius: 1,
                            height: 4,
                            backgroundColor: (theme) => theme.palette.grey[200],
                        }}
                        color="secondary"
                        variant="indeterminate"
                    />
                    <Typography variant="textSm" color="textTextSecondary" fontWeight={500}>
                        {formatMessage({ defaultMessage: "Uploading..." })}
                    </Typography>
                </Grid>
            )}
            <Stack direction="row" paddingBottom={0.5} pt={1} width="100%" justifyContent="space-between">
                <Box>
                    {touched && (
                        <Typography variant="textSm" color="textTextError">
                            {error}
                        </Typography>
                    )}
                </Box>

                {uploadType === FileUpload.Optional && (
                    <Typography variant="textXs" color="textTextHelper">
                        {formatMessage({ defaultMessage: "Optional upload" })}
                    </Typography>
                )}
                {uploadType === FileUpload.Required && (
                    <Typography variant="textXs" color="textTextHelper">
                        {formatMessage({ defaultMessage: "Required upload" })}
                    </Typography>
                )}
            </Stack>
            {!disabled && !loading && canUploadMoreFiles && (
                <UploadWrapper sx={{ display: "flex" }}>
                    <DropZone>
                        <UploadInput
                            type="file"
                            multiple={multiple}
                            accept={accept}
                            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                                // clear the input value so that the onChange event will fire even if the user selects the same file
                                e.currentTarget.value = "";
                            }}
                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                setLoading(true);
                                setSubmitBlocked(true);
                                try {
                                    await handleFileChange(e.target.files);
                                } finally {
                                    setSubmitBlocked(false);
                                    setLoading(false);
                                }
                            }}
                        />
                    </DropZone>
                </UploadWrapper>
            )}
        </Grid>
    );
};
